import PropTypes from "prop-types";
import React, { useEffect, useState, useRef } from "react";
import { withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Col, Dropdown, DropdownMenu, DropdownToggle, Row } from "reactstrap";
import { createSelector } from "reselect";
import SimpleBar from "simplebar-react";
import { formatDate } from "../../../helpers/formatDate";
import { getMyNotification, readMyNotification } from "../../../store/actions";
import { getTicketById } from "../../../store/ticket/actions";

const NotificationDropdown = (props) => {
  const [menu, setMenu] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const hasRequestedPermission = useRef(false);
  const notificationInstanceRef = useRef(null);
  const isAlarmPlayingRef = useRef(false);

  const notificationsSelector = createSelector(
    (state) => state.Common,
    (Common) => Common.notifications
  );
  const notifications = useSelector(notificationsSelector);

  // Request notification permission
  useEffect(() => {
    const requestNotificationPermission = async () => {
      if (!("Notification" in window)) {
        console.log("This browser does not support notifications");
        return;
      }

      if (Notification.permission === "granted") {
        setPermissionGranted(true);
      } else if (Notification.permission !== "denied" && !hasRequestedPermission.current) {
        hasRequestedPermission.current = true;
        
        try {
          const permission = await Notification.requestPermission();
          if (permission === "granted") {
            setPermissionGranted(true);
          }
        } catch (error) {
          console.error("Error requesting notification permission:", error);
        }
      }
    };

    requestNotificationPermission();
  }, []);

  // Fetch notifications
  useEffect(() => {
    dispatch(getMyNotification(1, 5));
  }, [dispatch]);

  // Create a function to play audio through notification or directly
  const playSosAlarm = () => {
    if (isAlarmPlayingRef.current) return;
    
    isAlarmPlayingRef.current = true;
    
    // Attempt multiple methods to alert the user
    
    // 1. Try browser notification with sound
    if (permissionGranted && "Notification" in window) {
      // Close previous notification if exists
      if (notificationInstanceRef.current) {
        notificationInstanceRef.current.close();
      }
      
      notificationInstanceRef.current = new Notification("SOS EMERGENCY ALERT", {
        body: "Urgent assistance required! Check notifications for details.",
        icon: "/path-to-emergency-icon.png", // Add your icon path
        silent: false, // Allow browser's default notification sound
      });
      
      notificationInstanceRef.current.onclick = () => {
        window.focus();
        navigate("/all-notifications");
      };
    }
    
    // 2. Try direct audio play
    if (audioRef.current) {
      // Set volume to maximum
      audioRef.current.volume = 1.0;
      
      // Try to play with user activation state hack
      const playPromise = audioRef.current.play();
      
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.log("Direct audio play failed:", error);
          
          // If direct play fails, try to play on next user interaction
          const handleInteraction = () => {
            audioRef.current.play().catch(e => console.log("Play on interaction failed:", e));
            
            // Only try once per user interaction
            window.removeEventListener("click", handleInteraction);
            window.removeEventListener("keydown", handleInteraction);
            window.removeEventListener("touchstart", handleInteraction);
          };
          
          window.addEventListener("click", handleInteraction);
          window.addEventListener("keydown", handleInteraction);
          window.addEventListener("touchstart", handleInteraction);
        });
      }
    }
    
    // 3. Visual indicators (already in your CSS via the sos-alert class)
  };

  const stopSosAlarm = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    
    if (notificationInstanceRef.current) {
      notificationInstanceRef.current.close();
      notificationInstanceRef.current = null;
    }
    
    isAlarmPlayingRef.current = false;
  };

  // Handle SOS alarm sounds
  useEffect(() => {
    const hasSosAlert = notifications?.some(
      (notification) => notification.isAlarm && !notification.isRead
    );

    if (hasSosAlert) {
      playSosAlarm();
    } else {
      stopSosAlarm();
    }
  }, [notifications, permissionGranted]);

  const handleRead = (id) => {
    dispatch(readMyNotification(id));
  };

  return (
    <React.Fragment>
      <audio 
        ref={audioRef} 
        src="/sos-alarm.mp3" 
        preload="auto"
        loop // Add loop for continuous alarm until acknowledged
      />
      
      <Dropdown
        isOpen={menu}
        toggle={() => setMenu(!menu)}
        className="dropdown d-inline-block"
        tag="li"
      >
        <DropdownToggle
          className="btn header-item noti-icon"
          tag="button"
          id="page-header-notifications-dropdown"
        >
          <i className="ri-notification-3-line" />
          {notifications?.some(notification => !notification.isRead) && (
            <span className="noti-dot"></span>
          )}
          {notifications?.some(notification => notification.isAlarm && !notification.isRead) && (
            <span className="sos-indicator"></span> /* Add styling for this in your CSS */
          )}
        </DropdownToggle>

        <DropdownMenu className="dropdown-menu-lg dropdown-menu-end p-0">
          <div className="p-3">
            <Row className="align-items-center">
              <Col>
                <h6 className="m-0">{props.t("Notifications")} </h6>
              </Col>
              <div className="col-auto">
                {notifications?.length > 0 && (
                  <Link to="/all-notifications" className="small">
                    View All
                  </Link>
                )}
              </div>
            </Row>
          </div>

          <SimpleBar style={{ height: "330px" }}>
            {notifications?.length > 0 ? (
              notifications.map((notification,index) => (
                <div
                  key={index}
                  onClick={() => {
                    dispatch(
                      getTicketById(notification.ticketId, navigate, true)
                    );
                    handleRead(notification.createdAt);
                  }}
                  className={`d-flex mb-1 p-2 ${
                    notification.isRead ? "" : "bg-soft-blue"
                  } ${notification.isAlarm && !notification.isRead ? "sos-alert" : ""}`}
                >
                  <div className="flex-shrink-0 me-3">
                    <div className="avatar-xs">
                      <span
                        className={`avatar-title ${
                          notification.isSos ? "bg-danger" : "bg-success"
                        } rounded-circle font-size-16`}
                      >
                        <i
                          className={
                            notification.isSos
                              ? "ri-alarm-warning-line"
                              : "ri-checkbox-circle-line"
                          }
                        ></i>
                      </span>
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h6 className="mb-1">{notification.title}</h6>
                    <div className="font-size-12 text-muted">
                      <p className="mb-1">{notification.message}</p>
                      <p className="mb-0">
                        <i className="mdi mdi-clock-outline"></i>{" "}
                        {formatDate(notification.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-3 text-center text-muted">
                No notifications available.
              </div>
            )}
          </SimpleBar>

          {notifications?.length > 0 && (
            <div className="p-2 border-top d-grid">
              <Link
                className="btn btn-sm btn-link font-size-14 text-center"
                to="/all-notifications"
              >
                <i className="mdi mdi-arrow-right-circle me-1"></i>{" "}
                <span key="t-view-more">{props.t("View More..")}</span>
              </Link>
            </div>
          )}
        </DropdownMenu>
      </Dropdown>
    </React.Fragment>
  );
};

NotificationDropdown.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(NotificationDropdown);